<template>
  <div
    :id="formattedId"
    :class="bem()"
  >
    <Swiper
      :class="bem('swiper')"
      v-bind="swiperOptions"
      @slide-change="handleSlideChanged"
    >
      <SwiperSlide
        v-for="(post, index) in instagramPosts"
        :key="post.id"
        :class="bem('slide')"
      >
        <InstagramCard
          :post="post"
          :position="index"
          :is-active="activePostId === post.id"
          :tracking-id="trackingId"
          :show-title="props.showProductTitle"
          :does-change-customised-product="props.doesChangeCustomisedProduct"
          @active-post-id="setActivePostId"
          @change-customised-product="handleChangeCustomisedProduct"
        />
      </SwiperSlide>
    </Swiper>
    <div :class="bem('swiper-controls')">
      <CarouselArrow
        arrow="left"
        :is-visible="hasHadInteraction && leftArrowVisible"
      />
      <CarouselArrow
        arrow="right"
        :is-visible="rightArrowVisible"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, Grid } from 'swiper/modules'
import {
  getInstagramPostsQuery,
  type InstagramPost,
} from '~/gql/instagramPosts.gql'

const bem = useBEM('InstagramGrid')
const emit = defineEmits(['change-customised-product', 'insufficient-posts-found'])
const productStore = useProductStore()

const props = defineProps({
  posts: { type: Number, default: 12 },
  products: { type: Array, default: () => [] },
  uniqueId: { type: String, default: null },
  desktopRows: { type: Number, default: 1 },
  trackingId: { type: String, default: 'Insta Shop' },
  rowPosition: { type: Number, default: 0 },
  showProductTitle: { type: Boolean, default: true },
  doesChangeCustomisedProduct: { type: Boolean, default: false },
})

const activePostId = ref<string | null>(null)
const hasHadInteraction = ref<boolean>(false)
const leftArrowVisible = ref<boolean>(false)
const rightArrowVisible = ref<boolean>(true)

const variables = {
  limit: props.posts,
  offset: 0,
  ...(props.products.length && { baseProducts: props.products }),
} as Record<string, any>

const { data, error } = await useDatoQuery({
  query: getInstagramPostsQuery,
  variables,
  prefetchedRequest: [
    'instagramPosts',
    props.posts,
    ...props.products.join('-'),
  ].join('-'),
})

if (error.value) {
  const { $sentry } = useNuxtApp()
  $sentry.captureException(error)
  console.error(error)
}

const instagramPosts = data.value.allInstagramPosts as InstagramPost[]

if (instagramPosts.length < 6 && props.products.length) {
  emit('insufficient-posts-found')
}

instagramPosts.forEach((post) => {
  if (!post.baseProduct?.sku) return

  post.sku = [
    post.baseProduct.sku,
    ...(post.customProductParts?.map((part) => part.sku) || []),
  ]
    .filter(Boolean)
    .join('-')

  post.url = `${post.baseProduct.url}?sku=${post.sku}`
  post.productTitle = productStore.productName(post.baseProduct.sku)
})

const formattedId = computed(
  () =>
    `insta-shop--${
      props.uniqueId?.toLowerCase()?.replace(/\s+/g, '-') ?? 'insta-shop-change'
    }`
)

function setActivePostId(id: string) {
  activePostId.value = id
}

function handleSlideChanged(swiper: any) {
  leftArrowVisible.value = props.desktopRows > 1 ? !swiper.isBeginning : true
  rightArrowVisible.value = !swiper.isEnd
  hasHadInteraction.value = true
}

function handleChangeCustomisedProduct(sku: string) {
  emit('change-customised-product', sku)
}

const swiperOptions = ref({
  modules: [Navigation, Grid],
  slidesPerView: 1.5,
  spaceBetween: 0,
  speed: 300,
  loop: true,
  centeredSlides: true,
  breakpoints: {
    650: { slidesPerView: 2.5, centeredSlides: false },
    800: { slidesPerView: 3.5, centeredSlides: false },
    1150: { slidesPerView: 4.5, centeredSlides: false },
    1500: { slidesPerView: 5.5, centeredSlides: false },
  },
  navigation: {
    nextEl: `#${formattedId.value} [data-carousel-arrow="right"]`,
    prevEl: `#${formattedId.value} [data-carousel-arrow="left"]`,
  },
})
</script>

<style lang="postcss">
.InstagramGrid {
  @apply overflow-x-clip overflow-y-visible relative w-full;
}

.InstagramGrid__swiper {
  @apply w-full overflow-clip;
}

.InstagramGrid__slide {
  @apply aspect-square w-[calc(100vw_/_1.5)]
  sm:w-[calc(100vw_/_2.5)]
  md:w-[calc(100vw_/_3.5)]
  xl:w-[calc(100vw_/_4.5)]
  xxl:w-[calc(100vw_/_5.5)];
}

.InstagramGrid__swiper-controls {
  @apply absolute z-[2] -top-16 right-[var(--outer-gutter)]
  hidden md:flex items-center gap-2 justify-center;
}
</style>
